/**
 * F 331 — THE ENGINE STATE.
 *
 * F 329 wrote what is alive. F 330 wrote what each model can do. **Nothing in
 * the UI reads either of those directly** — this module is the one place that
 * turns graph nodes into a view, so the view can be tested without a browser
 * and so there is exactly one answer to "what can this engine do".
 *
 * THE RULE THIS MODULE EXISTS TO ENFORCE
 * ---------------------------------------
 * **An unknown capability is GREY. It is never quietly green.**
 *
 * The dispatch said "do not fake the pie chart" and that is not a style note —
 * it is the whole difference between a dashboard and a decoration. A slice goes
 * green only when a MEASURED, ALIVE, non-benched model provides it. If no node
 * says so, the slice is missing, and a missing slice is the most useful thing
 * on the screen: it is the thing the user can fix.
 *
 * Every slice carries the evidence that produced it, so a reader can see WHY it
 * is green rather than trusting a colour.
 */

import type { ModelCapabilities } from './capabilities';
import type { CooldownRecord } from './cooldown';

/** The four slices the dispatch asks for. */
export type CapabilityName = 'text' | 'vision' | 'embeddings' | 'reasoning';

export const CAPABILITIES: CapabilityName[] = ['text', 'vision', 'embeddings', 'reasoning'];

/**
 * strong  — alive, not benched, comfortable headroom
 * limited — alive but close to a limit, benched, or low-confidence
 * missing — nothing measured provides it, or the provider is not connected
 */
export type CapabilityHealth = 'strong' | 'limited' | 'missing';

export interface CapabilitySlice {
  capability: CapabilityName;
  health: CapabilityHealth;
  /** The model that best serves this capability, or null when missing. */
  bestModel: string | null;
  provider: string | null;
  /** Why this slice is this colour — always populated, never empty. */
  reason: string;
  latencyMs: number | null;
  /** Whether this is real or missing, for the colour legend. */
  evidence: 'measured' | 'none';
}

export interface HealthBar {
  provider: string;
  model: string;
  rpmLimit: number | null;
  rpmRemaining: number | null;
  /** 0..1, or null when the provider reports nothing. */
  rpmFraction: number | null;
  status: 'ok' | 'near-limit' | 'benched' | 'unknown';
  state: CooldownRecord['state'];
  cooldownUntil: number | null;
}

export interface UsageEntry {
  provider: string;
  model: string;
  tokens: number;
  /** From F 330's tokenizer: exact-reported | exact-api | estimated-local */
  countType: 'exact-reported' | 'exact-api' | 'estimated-local' | 'unknown';
  at: number;
}

export interface UsageBucket {
  label: string;
  total: number;
  byModel: { model: string; provider: string; tokens: number; estimated: boolean }[];
}

export type SlotStatus = 'not_connected' | 'connected';

export interface ProviderSlot {
  provider: string;
  label: string;
  status: SlotStatus;
  /** What this provider would unlock, when we know. Unknown stays unknown. */
  unlocks: CapabilityName[];
  note?: string;
}

export interface EngineState {
  slices: CapabilitySlice[];
  health: HealthBar[];
  slots: ProviderSlot[];
  usage: UsageBucket[];
  generatedAt: number;
  /** Models that exist but are currently benched — the routing consequence. */
  benchedCount: number;
  connectedProviders: string[];
}

const EMPTY_REASON = 'no measured model provides this capability';

/** Does this model serve this capability? Measured, never inferred from a name. */
export function provides(c: ModelCapabilities, cap: CapabilityName): boolean {
  if (c.lastTestResult !== 'alive') return false;
  switch (cap) {
    case 'text':
      return c.shape === 'chat';
    case 'vision':
      // F 330 measured this per model by sending it a real PNG.
      return c.modality === 'text+image';
    case 'reasoning':
      return c.reasoningIntensity === 'high' || c.reasoningIntensity === 'low';
    case 'embeddings':
      // F 339. F 330 had nothing to measure here and hardcoded `false`, which
      // is why the slice stayed grey forever. It is now READ from evidence: a
      // model provides embeddings only when the embedding TRANSPORT answered it
      // and returned a real vector, which `classifyShape` records as the
      // 'embedding' shape.
      //
      // This deliberately does NOT test `lastTestResult === 'alive'` alone.
      // Every chat model is alive, so that test would light the slice using
      // models that were never asked for a vector — a green slice that is a
      // lie. Measured, never inferred.
      return c.shape === 'embedding';
    default:
      return false;
  }
}

/**
 * Build the four slices.
 *
 * Ranking is measured-only: a `strong` candidate beats a `limited` one, and
 * within the same band the faster model wins. A benched model is never the best
 * answer, whatever its raw latency.
 */
export function buildSlices(
  caps: ModelCapabilities[],
  cooldowns: Map<string, CooldownRecord>,
  now: number,
  rpmRemaining: Map<string, number | null> = new Map(),
): CapabilitySlice[] {
  return CAPABILITIES.map((cap) => {
    const candidates = caps.filter((c) => provides(c, cap));
    if (candidates.length === 0) {
      return {
        capability: cap, health: 'missing', bestModel: null, provider: null,
        reason: EMPTY_REASON, latencyMs: null, evidence: 'none',
      };
    }
    const scored = candidates
      .map((c) => ({ c, band: bandFor(c, cooldowns, now, rpmRemaining) }))
      .sort((a, b) => {
        if (a.band !== b.band) return a.band - b.band;
        return (a.c.lastLatencyMs ?? Infinity) - (b.c.lastLatencyMs ?? Infinity);
      });
    const best = scored[0];
    const health: CapabilityHealth = best.band === 0 ? 'strong' : 'limited';
    return {
      capability: cap,
      health,
      bestModel: best.c.model,
      provider: best.c.provider,
      reason: reasonFor(best.c, cap, health, cooldowns, now),
      latencyMs: best.c.lastLatencyMs ?? null,
      evidence: 'measured',
    };
  });
}

function bandFor(
  c: ModelCapabilities,
  cooldowns: Map<string, CooldownRecord>,
  now: number,
  rpmRemaining: Map<string, number | null>,
): number {
  const rec = cooldowns.get(`${c.provider}::${c.model}`);
  if (rec?.cooldownUntil != null && now < rec.cooldownUntil) return 2;
  const left = rpmRemaining.get(`${c.provider}::${c.model}`);
  if (typeof left === 'number' && c.rpmLimit && left / c.rpmLimit < 0.2) return 1;
  return 0;
}

function reasonFor(
  c: ModelCapabilities,
  cap: CapabilityName,
  health: CapabilityHealth,
  cooldowns: Map<string, CooldownRecord>,
  now: number,
): string {
  const rec = cooldowns.get(`${c.provider}::${c.model}`);
  if (rec?.cooldownUntil != null && now < rec.cooldownUntil) {
    return `best option is benched until ${new Date(rec.cooldownUntil).toISOString()}`;
  }
  const bits: string[] = [];
  if (cap === 'vision') bits.push('answered a real image probe');
  if (cap === 'reasoning') bits.push(`${c.reasoningIntensity} reasoning`);
  if (health === 'limited') bits.push('near a limit');
  return bits.length ? bits.join(', ') : `${c.lastTestResult}, ${c.lastLatencyMs ?? '?'}ms`;
}

/** RPM headroom per model. Green > 20%, yellow < 20%, red when benched. */
export function buildHealth(
  caps: ModelCapabilities[],
  cooldowns: Map<string, CooldownRecord>,
  rpmRemaining: Map<string, number | null>,
  now: number,
): HealthBar[] {
  return caps.map((c) => {
    const key = `${c.provider}::${c.model}`;
    const rec = cooldowns.get(key);
    const benched = rec?.cooldownUntil != null && now < rec.cooldownUntil;
    const left = rpmRemaining.has(key) ? rpmRemaining.get(key)! : null;
    const frac = c.rpmLimit && typeof left === 'number' ? left / c.rpmLimit : null;
    const status: HealthBar['status'] = benched
      ? 'benched'
      : frac === null
        ? 'unknown'
        : frac < 0.2
          ? 'near-limit'
          : 'ok';
    return {
      provider: c.provider, model: c.model,
      rpmLimit: c.rpmLimit, rpmRemaining: left, rpmFraction: frac,
      status, state: rec?.state ?? 'ready', cooldownUntil: rec?.cooldownUntil ?? null,
    };
  });
}

/** Bucket usage by day or week. `estimated` drives the ~ marker in the UI. */
export function buildUsage(
  entries: UsageEntry[],
  window: 'today' | 'week',
  now: number,
): UsageBucket[] {
  const dayMs = 86_400_000;
  const today = Math.floor(now / dayMs) * dayMs;
  const buckets = new Map<number, UsageEntry[]>();
  for (let i = 0; i < 7; i++) buckets.set(today - i * dayMs, []);
  const from = window === 'today' ? today : today - 6 * dayMs;
  for (const e of entries) {
    const d = Math.floor(e.at / dayMs) * dayMs;
    if (d >= from && buckets.has(d)) buckets.get(d)!.push(e);
  }
  return [...buckets.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([day, es]) => {
      const byModel = new Map<string, UsageBucket['byModel'][number]>();
      for (const e of es) {
        const k = `${e.provider}::${e.model}`;
        const cur = byModel.get(k) ?? {
          model: e.model, provider: e.provider, tokens: 0,
          estimated: e.countType === 'estimated-local' || e.countType === 'unknown',
        };
        cur.tokens += e.tokens;
        byModel.set(k, cur);
      }
      return {
        label: new Date(day).toISOString().slice(5, 10),
        total: es.reduce((n, e) => n + e.tokens, 0),
        byModel: [...byModel.values()].sort((a, b) => b.tokens - a.tokens),
      };
    });
}

/** Assemble everything the dashboard renders. */
export function buildEngineState(input: {
  caps: ModelCapabilities[];
  cooldowns?: Map<string, CooldownRecord>;
  slots?: ProviderSlot[];
  rpmRemaining?: Map<string, number | null>;
  usage?: UsageEntry[];
  now?: number;
  window?: 'today' | 'week';
}): EngineState {
  const now = input.now ?? Date.now();
  const cooldowns = input.cooldowns ?? new Map<string, CooldownRecord>();
  const rpmRemaining = input.rpmRemaining ?? new Map<string, number | null>();
  const slices = buildSlices(input.caps, cooldowns, now, rpmRemaining);
  return {
    slices,
    health: buildHealth(input.caps, cooldowns, rpmRemaining, now),
    slots: input.slots ?? [],
    usage: buildUsage(input.usage ?? [], input.window ?? 'today', now),
    generatedAt: now,
    benchedCount: [...cooldowns.values()].filter(
      (r) => r.cooldownUntil !== null && now < r.cooldownUntil,
    ).length,
    connectedProviders: [...new Set(input.caps.map((c) => c.provider))].sort(),
  } as EngineState & { usage: UsageBucket[] };
}

/** A capability with nothing measured is the one worth offering a Power Up. */
export function missingCapabilities(state: EngineState): CapabilityName[] {
  return state.slices.filter((s) => s.health === 'missing').map((s) => s.capability);
}

export function limitedCapabilities(state: EngineState): CapabilityName[] {
  return state.slices.filter((s) => s.health === 'limited').map((s) => s.capability);
}

/** The slice's slice of the pie, in degrees. Missing slices get a floor. */
export function sliceAngles(slices: CapabilitySlice[]): { capability: CapabilityName; degrees: number; start: number }[] {
  // A missing capability still occupies a visible arc, or the pie would silently
  // omit the thing the user most needs to see.
  const weights = slices.map((s) => (s.health === 'missing' ? 1 : 3));
  const total = weights.reduce((a, b) => a + b, 0) || 1;
  let acc = 0;
  return slices.map((s, i) => {
    const degrees = (weights[i] / total) * 360;
    const start = acc;
    acc += degrees;
    return { capability: s.capability, degrees, start };
  });
}

